import React, { useMemo, useState } from "react";
import NumberFormat from "react-number-format";
import { toast } from "react-toastify";

import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete, Box, Button, IconButton, Stack, TextField, Typography } from "@mui/material";
import { useDidUpdate } from "hooks";
import { debounce } from "lodash";
import { AccountSettings, MeasureType } from "store/types";

import { MeasuresSelect } from "./MeasuresSelect";

type Rules = {
  id: number;
} & AccountSettings["validation_settings"]["rules"][number];

type State = {
  rules: Rules[];
  message: string | null;
};
interface Props {
  rules: Rules[];
  message: string | null;
  updating: boolean;
  updateSettings: (data: Partial<AccountSettings>) => Promise<AccountSettings>;
}

const thresholds = [
  {
    value: "absolute",
    label: "Absolute"
  },
  {
    value: "relative",
    label: "Relative"
  },
  {
    value: "both",
    label: "Both parameters"
  }
];

const MAX_INPUTS = 4;
const GAP_BETWEEN_INPUTS = 10;

export const MeasureDataValidation: React.FC<Props> = ({ rules = [], message = null, updating, updateSettings }) => {
  const [state, setState] = useState<State>({ rules, message });

  const addValidator = () => {
    const maxId = state.rules.reduce((max, validator) => Math.max(max, Number(validator.id)), 0);
    setState((prev) => ({
      ...prev,
      rules: [
        ...prev.rules,
        { id: maxId + 1, measure_type: null, key: null, threshold: null, absolute_value: null, relative_value: null }
      ]
    }));
  };

  const removeValidator = (id: number) => {
    setState((prev) => ({
      ...prev,
      rules: prev.rules.filter((validator) => validator.id !== id)
    }));
  };

  const handlePickMeasure = ({
    id,
    option
  }: {
    id: number;
    option: { measure_type: MeasureType; key: string } | null;
  }) => {
    setState((prev) => ({
      ...prev,
      rules: prev.rules.map((item) => (item.id === id ? { ...item, ...option } : item))
    }));
  };

  const handleChangeThreshold = ({ id, value }: { id: number; value: string | null }) => {
    setState((prev) => ({
      ...prev,
      rules: prev.rules.map((item) => {
        if (item.id !== id) return item;
        return { ...item, threshold: value, absolute_value: null, relative_value: null };
      })
    }));
  };

  const handleChangeNumbers = ({
    id,
    key,
    value
  }: {
    id: number;
    key: "absolute_value" | "relative_value";
    value: number | null;
  }) => {
    setState((prev) => ({
      ...prev,
      rules: prev.rules.map((item) => {
        if (item.id !== id) return item;
        return { ...item, [key]: value };
      })
    }));
  };

  const inputWidth = `calc((100% - ${GAP_BETWEEN_INPUTS * (MAX_INPUTS - 1)}px) / ${MAX_INPUTS})`;

  const debounceSendData = useMemo(
    () =>
      debounce((state: State) => {
        if (!state.message) {
          return toast.info("The message (Warning text) is required");
        }
        const dataToSend = {
          validation_settings: {
            ...state,
            rules: state.rules
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              .map(({ id, ...rest }) => {
                return rest;
              })
              .filter((rule) => rule.measure_type !== null && rule.key !== null && rule.threshold !== null)
          }
        };
        updateSettings(dataToSend);
      }, 1000),
    []
  );

  useDidUpdate(() => {
    debounceSendData(state);
  }, [state]);

  return (
    <Stack
      sx={{
        gap: "16px",
        bgcolor: "#FFFFFF",
        p: "16px",
        boxShadow: "sm",
        borderRadius: "8px",
        color: "#071F35"
      }}
    >
      <Stack>
        <Typography variant="h6">Validation KPIs</Typography>
        <Typography variant="body1">
          Customize the validation panel for better indication of potential errors. Up to 3 validators available.
        </Typography>
      </Stack>
      <Stack sx={{ gap: "20px" }}>
        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
          Validators
        </Typography>
        {state.rules.map((rule, index) => (
          <Stack
            key={index}
            sx={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", gap: "10px" }}
          >
            <Stack sx={{ flexGrow: 1, flexDirection: "row", gap: "10px", alignItems: "center" }}>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {index + 1}.
              </Typography>
              <Stack sx={{ flexDirection: "row", gap: "10px", alignItems: "center", flexGrow: 1 }}>
                <MeasuresSelect
                  sx={{ width: inputWidth }}
                  disabled={updating}
                  value={rule?.measure_type && rule?.key ? { measure_type: rule.measure_type, key: rule.key } : null}
                  onChange={(value) => handlePickMeasure({ id: rule.id, option: value })}
                />
                {rule.measure_type && rule.key && (
                  <Autocomplete
                    sx={{ width: inputWidth }}
                    disabled={updating}
                    options={thresholds}
                    disableClearable
                    value={thresholds.find((t) => t.value === rule.threshold)}
                    renderInput={(params) => <TextField {...params} label="Threshold" />}
                    onChange={(_, value) => {
                      handleChangeThreshold({ id: rule.id, value: value?.value as string | null });
                    }}
                  />
                )}
                {(rule.threshold === "absolute" || rule.threshold === "both") && (
                  <NumberFormat
                    sx={{ width: inputWidth }}
                    label="Absolute Value"
                    thousandSeparator
                    decimalScale={2}
                    disabled={updating}
                    allowNegative={false}
                    customInput={TextField}
                    value={rule.absolute_value}
                    onValueChange={({ floatValue }) => {
                      handleChangeNumbers({
                        id: rule.id,
                        key: "absolute_value",
                        value: floatValue || null
                      });
                    }}
                  />
                )}
                {(rule.threshold === "relative" || rule.threshold === "both") && (
                  <NumberFormat
                    sx={{ width: inputWidth }}
                    label="Relative Value"
                    decimalScale={2}
                    suffix="%"
                    disabled={updating}
                    max={100}
                    allowNegative={false}
                    customInput={TextField}
                    value={rule.relative_value}
                    isAllowed={(values) => {
                      const { floatValue } = values;
                      if (!floatValue) return true;
                      return floatValue >= 0 && floatValue <= 100;
                    }}
                    onValueChange={({ floatValue }) => {
                      handleChangeNumbers({
                        id: rule.id,
                        key: "relative_value",
                        value: floatValue || null
                      });
                    }}
                  />
                )}
              </Stack>
            </Stack>
            <Box sx={{ width: 32, height: 32 }}>
              {state.rules.length > 1 && (
                <IconButton size="sm" onClick={() => removeValidator(rule.id)}>
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          </Stack>
        ))}
        {state.rules.length < 3 && (
          <Button sx={{ color: "#26A6AE", alignSelf: "flex-start" }} onClick={addValidator}>
            Add validator
          </Button>
        )}
      </Stack>
      <Stack sx={{ gap: "20px" }}>
        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
          Message
        </Typography>
        <TextField
          required
          fullWidth
          disabled={updating}
          label="Warning Text"
          value={state.message}
          onChange={(e) => setState((prev) => ({ ...prev, message: e.target.value }))}
        />
      </Stack>
    </Stack>
  );
};
